// Select múltiplo com busca em tempo real — pra listas grandes de opções fixas
// onde a pessoa pode escolher mais de um item (ex: Comissões da OAB/MS).
// Value é sempre um array de strings.

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { colors } from '../../../assets/styles/GlobalStyles';

const Container = styled.div`
  width: 100%;
  margin-bottom: 1.2rem;
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${colors.darkBlue};
  font-weight: 600;
  font-size: 0.9rem;
  span { color: #ff4444; }
`;

const ControlBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
  width: 100%;
  min-height: 3.1rem;
  padding: 0.5rem 0.6rem;
  border: 2px solid ${props => props.$hasError ? '#ff4444' : '#e8ecef'};
  border-radius: 8px;
  background: ${props => props.disabled ? '#f5f5f5' : colors.backgroundLight};
  box-sizing: border-box;
  cursor: text;

  &:focus-within {
    border-color: ${props => props.$hasError ? '#ff4444' : colors.lightBlue};
    background: white;
  }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: ${colors.lightBlue};
  color: white;
  border-radius: 20px;
  padding: 0.3rem 0.4rem 0.3rem 0.7rem;
  font-size: 0.82rem;
  line-height: 1.2;
  max-width: 100%;
`;

const TagRemoveButton = styled.button`
  background: rgba(255, 255, 255, 0.25);
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;

  &:hover { background: rgba(255, 255, 255, 0.45); }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.95rem;
  padding: 0.3rem 0;
  color: #333;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  &::placeholder { color: #999; }
`;

const SearchIcon = styled(FaSearch)`
  color: #999;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

const OptionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 260px;
  overflow-y: auto;
  background: white;
  border: 1px solid #e8ecef;
  border-radius: 8px;
  margin-top: 4px;
  padding: 0;
  list-style: none;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const OptionItem = styled.li`
  padding: 0.8rem 1rem;
  cursor: pointer;
  font-size: 0.95rem;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.lightBlue};
    color: white;
  }

  &:last-child { border-bottom: none; }
`;

const NoOptions = styled.div`
  padding: 1rem;
  text-align: center;
  color: #999;
  font-size: 0.9rem;
`;

const ErrorMessage = styled.small`
  color: #ff4444;
  font-size: 0.8rem;
  margin-top: 0.3rem;
  display: block;
`;

/**
 * @param {Array<string>} value - valores selecionados
 * @param {Function} onChange - chamado com { target: { name, value: novoArray } }
 */
export const MultiSearchSelect = ({
  label,
  name,
  options = [],
  value = [],
  onChange,
  onBlur,
  error,
  required,
  disabled,
  placeholder = 'Digite para buscar...',
  noOptionsMessage = 'Nenhuma opção encontrada'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedValues = Array.isArray(value) ? value : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const emitChange = (novoArray) => {
    onChange({ target: { name, value: novoArray } });
  };

  const handleAdd = (optionValue) => {
    if (!selectedValues.includes(optionValue)) {
      emitChange([...selectedValues, optionValue]);
    }
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const handleRemove = (optionValue) => {
    emitChange(selectedValues.filter((v) => v !== optionValue));
  };

  const handleBoxClick = () => {
    if (!disabled) {
      setIsOpen(true);
      inputRef.current?.focus();
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
      }
    }, 150);
    onBlur?.({ target: { name, value: selectedValues } });
  };

  const filteredOptions = options.filter((option) =>
    !selectedValues.includes(option.value) &&
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSelectedLabel = (optionValue) =>
    options.find((o) => o.value === optionValue)?.label || optionValue;

  return (
    <Container ref={containerRef}>
      {label && (
        <Label>
          {label} {required && <span>*</span>}
        </Label>
      )}

      <ControlBox $hasError={!!error} disabled={disabled} onClick={handleBoxClick}>
        {selectedValues.map((optionValue) => (
          <Tag key={optionValue}>
            {getSelectedLabel(optionValue)}
            {!disabled && (
              <TagRemoveButton
                type="button"
                onClick={(e) => { e.stopPropagation(); handleRemove(optionValue); }}
                aria-label={`Remover ${getSelectedLabel(optionValue)}`}
              >
                <FaTimes size={10} />
              </TagRemoveButton>
            )}
          </Tag>
        ))}
        <SearchIcon />
        <SearchInput
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          onBlur={handleInputBlur}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setIsOpen(false);
            if (e.key === 'Enter' && filteredOptions.length === 1) {
              e.preventDefault();
              handleAdd(filteredOptions[0].value);
            }
          }}
          placeholder={selectedValues.length === 0 ? placeholder : ''}
          disabled={disabled}
          autoComplete="off"
        />
      </ControlBox>

      {isOpen && !disabled && (
        <OptionsList>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <OptionItem
                key={option.value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleAdd(option.value)}
              >
                {option.label}
              </OptionItem>
            ))
          ) : (
            <NoOptions>{noOptionsMessage}</NoOptions>
          )}
        </OptionsList>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  );
};

